/**
 * An export that is not coming.
 *
 * `getExport` derives `pending` from the absence of the object in the bucket, so the
 * same status covers "the queue has not run yet" and "the queue never will" — nothing
 * marks a request abandoned. Only the clock tells them apart, and the queue runs within
 * seconds of the request, so two minutes is not a slow export; it is a lost one.
 *
 * Here rather than in the screen so the boundary is testable without a fake clock in a
 * React tree, and here rather than in `@saysooner/core` because no other client calls
 * `getExport` — iOS and Android carry the DTO and never ask for one.
 */
export const EXPORT_STALLED_AFTER_MS = 120_000;

export interface ExportStatusLike {
  readonly status: 'pending' | 'ready' | 'failed';
  readonly requestedAt: number;
}

export function isExportStalled(status: ExportStatusLike, now: number = Date.now()): boolean {
  // `failed` has been in the contract from the start with no producer. Honouring it
  // costs nothing and means a future producer needs no second change here.
  if (status.status === 'failed') return true;
  if (status.status !== 'pending') return false;
  return now - status.requestedAt > EXPORT_STALLED_AFTER_MS;
}

/** What the settings screen is telling the learner about their export. */
export type ExportPhase = 'none' | 'building' | 'lost' | 'ready';

/**
 * The one answer the three banners read.
 *
 * Written as a single phase rather than three conditions because the conditions were
 * `status !== 'ready'` and nothing else — so the lost case had no condition of its own
 * and fell into "we're putting it together". A phase cannot have that gap: every input
 * returns exactly one of these.
 */
export function exportPhase(
  requested: boolean,
  status: ExportStatusLike | undefined,
  now: number = Date.now(),
): ExportPhase {
  if (!requested) return 'none';
  // The first poll is still in flight: it is building until the server says otherwise.
  if (status === undefined) return 'building';
  if (status.status === 'ready') return 'ready';
  return isExportStalled(status, now) ? 'lost' : 'building';
}
